import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Megaphone, X } from 'lucide-react';
import type { Announcement, Language } from '../../types';
import { _t, pickI18nText } from '../../lib/i18n';
import { api } from '../../lib/api';
import { pickAnnouncementContent } from '../../lib/announcementContent';
import { ANNOUNCEMENTS_UPDATED_EVENT } from '../../lib/announcementEvents';
import { countUnreadAnnouncements, latestAnnouncementSentAt } from '../../lib/newsUnread';
import { loadNewsReadState, markNewsItemRead, markNewsRead, isAnnouncementListUnread, announcementVersionIso } from '../../lib/newsReadStorage';
import { SafeMarkdownContent } from '../common/SafeMarkdownContent';
import './visitingCardNews.css';

export type VisitingCardNewsApi = {
  announcements: Announcement[];
  unreadCount: number;
  hasNews: boolean;
  openList: () => void;
};

const VisitingCardNewsContext = createContext<VisitingCardNewsApi | null>(null);

export function useVisitingCardNewsContext(): VisitingCardNewsApi | null {
  return useContext(VisitingCardNewsContext);
}

/** Short date + time; year only when not current. */
function formatAnnouncementDate(iso: string | null | undefined, lang: Language): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const locale = lang === 'he' ? 'he-IL' : lang === 'en' ? 'en-GB' : 'ru-RU';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit',
  });
}

function teaserText(contentMd: string, maxLen = 72): string {
  const plain = contentMd
    .replace(/^#0\s+/gm, '')
    .replace(/^#{1,3}\s+/gm, '')
    .replace(/[*_~`#]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= maxLen) return plain;
  return `${plain.slice(0, maxLen - 1)}…`;
}

/** Newest-first slice so the sheet stays short inside the phone frame. */
const VISITING_CARD_NEWS_LIST_LIMIT = 5;

/**
 * Public /c news: fetch sent announcements, unread attention, list + fullscreen sheets.
 * First visit treats existing items as unread until the guest opens the list.
 */
function useVisitingCardNewsState(
  lang: Language,
  tenantSlug: string,
): VisitingCardNewsApi & { NewsOverlays: () => ReactNode } {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [listOpen, setListOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [lastReadAt, setLastReadAt] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [readAtById, setReadAtById] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!tenantSlug) {
      setLastReadAt(null);
      setInitialized(false);
      setReadAtById({});
      return;
    }
    const state = loadNewsReadState(tenantSlug);
    setLastReadAt(state?.lastReadAt ?? null);
    setInitialized(state?.initialized === true);
    setReadAtById(state?.readAtById ?? {});
  }, [tenantSlug]);

  const refresh = useCallback(async () => {
    if (!tenantSlug) {
      setAnnouncements([]);
      return;
    }
    try {
      const res = await api.listPublicAnnouncements(tenantSlug);
      setAnnouncements(res.announcements);
    } catch {
      setAnnouncements([]);
    }
  }, [tenantSlug]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onUpdated = () => {
      void refresh();
    };
    window.addEventListener(ANNOUNCEMENTS_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(ANNOUNCEMENTS_UPDATED_EVENT, onUpdated);
  }, [refresh]);

  const unreadCount = useMemo(() => {
    if (announcements.length === 0) return 0;
    if (!initialized || !lastReadAt) return announcements.length;
    return countUnreadAnnouncements(announcements, lastReadAt, true);
  }, [announcements, initialized, lastReadAt]);

  const markAllRead = useCallback(() => {
    if (!tenantSlug) return;
    const latest = latestAnnouncementSentAt(announcements) || new Date().toISOString();
    markNewsRead(tenantSlug, latest);
    setLastReadAt(latest);
    setInitialized(true);
  }, [announcements, tenantSlug]);

  const openList = useCallback(() => {
    setListOpen(true);
    setActiveId(null);
    void (async () => {
      if (!tenantSlug) {
        markAllRead();
        return;
      }
      try {
        const res = await api.listPublicAnnouncements(tenantSlug);
        setAnnouncements(res.announcements);
        const latest = latestAnnouncementSentAt(res.announcements) || new Date().toISOString();
        markNewsRead(tenantSlug, latest);
        setLastReadAt(latest);
        setInitialized(true);
      } catch {
        markAllRead();
      }
    })();
  }, [markAllRead, tenantSlug]);

  const openDetail = useCallback(
    (id: string) => {
      setActiveId(id);
      if (!tenantSlug) return;
      const item = announcements.find((row) => row.id === id);
      const version =
        (item ? announcementVersionIso(item) : null) || new Date().toISOString();
      setReadAtById(markNewsItemRead(tenantSlug, id, version));
    },
    [announcements, tenantSlug],
  );

  const closeAll = useCallback(() => {
    setListOpen(false);
    setActiveId(null);
  }, []);

  const active = useMemo(
    () => announcements.find((row) => row.id === activeId) || null,
    [announcements, activeId],
  );

  const listAnnouncements = useMemo(
    () => announcements.slice(0, VISITING_CARD_NEWS_LIST_LIMIT),
    [announcements],
  );

  const NewsOverlays = useCallback(() => {
    if (!listOpen && !active) return null;

    if (active) {
      const title = pickI18nText(active.titleI18n, lang) || _t('announcements.untitled', lang);
      const dateLabel = formatAnnouncementDate(active.sentAt || active.createdAt, lang);
      return (
        <div
          id="visiting-card-news-detail"
          className="absolute inset-0 z-[80] flex flex-col bg-[var(--bg-primary)] text-[var(--text-primary)]"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          dir={lang === 'he' ? 'rtl' : 'ltr'}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="relative flex shrink-0 items-center gap-3 border-b border-[var(--border-color)] bg-[var(--dialog-chrome,var(--bg-secondary))] px-4 py-3 pe-12">
            <button
              type="button"
              id="btn-visiting-card-news-back"
              onClick={() => setActiveId(null)}
              className="text-sm font-medium text-[var(--accent-primary)]"
            >
              {_t('visitingCard.newsBack', lang)}
            </button>
            <h2 className="min-w-0 flex-1 truncate text-base font-semibold">{title}</h2>
            <button
              type="button"
              id="btn-visiting-card-news-close-detail"
              aria-label={_t('common.close', lang)}
              onClick={closeAll}
              className="absolute end-3 top-3 rounded-full p-2 text-[var(--text-secondary)]"
            >
              <X size={20} />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            {dateLabel ? (
              <time className="mb-3 block text-xs text-[var(--text-secondary)]">{dateLabel}</time>
            ) : null}
            {active.imageUrl ? (
              <img
                src={active.imageUrl}
                alt=""
                className="mb-4 max-h-[40vh] w-full rounded-lg object-contain bg-[var(--bg-secondary)]"
              />
            ) : null}
            <SafeMarkdownContent
              markdown={pickAnnouncementContent(active, lang)}
              className="text-sm"
            />
          </div>
        </div>
      );
    }

    return (
      <div
        id="visiting-card-news-list"
        className="absolute inset-0 z-[80] flex flex-col justify-end bg-black/40"
        role="dialog"
        aria-modal="true"
        aria-label={_t('visitingCard.newsTitle', lang)}
        onClick={closeAll}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div
          className="flex max-h-[min(52vh,22rem)] flex-col overflow-hidden rounded-t-2xl bg-[var(--bg-primary)] text-[var(--text-primary)] shadow-xl"
          dir={lang === 'he' ? 'rtl' : 'ltr'}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative flex shrink-0 items-center border-b border-[var(--border-color)] px-4 py-3 pe-12">
            <h2 className="text-base font-semibold">{_t('visitingCard.newsTitle', lang)}</h2>
            <button
              type="button"
              id="btn-visiting-card-news-close-list"
              aria-label={_t('common.close', lang)}
              onClick={closeAll}
              className="absolute end-3 top-2.5 rounded-full p-2 text-[var(--text-secondary)]"
            >
              <X size={20} />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {listAnnouncements.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-[var(--text-secondary)]">
                {_t('visitingCard.newsEmpty', lang)}
              </p>
            ) : (
              <ul className="divide-y divide-[var(--border-color)]">
                {listAnnouncements.map((item) => {
                  const title =
                    pickI18nText(item.titleI18n, lang) || _t('announcements.untitled', lang);
                  const dateLabel = formatAnnouncementDate(item.sentAt || item.createdAt, lang);
                  const teaser = teaserText(pickAnnouncementContent(item, lang));
                  const isUnread = isAnnouncementListUnread(item, readAtById);
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        id={`btn-visiting-card-news-teaser-${item.id}`}
                        onClick={() => openDetail(item.id)}
                        className="flex w-full gap-3 px-4 py-3 text-start transition-colors hover:bg-[var(--bg-secondary)]"
                        aria-label={
                          isUnread
                            ? `${title}. ${_t('visitingCard.newsUnread', lang)}`
                            : `${title}. ${_t('visitingCard.newsRead', lang)}`
                        }
                      >
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt=""
                            className="h-14 w-14 shrink-0 rounded-lg object-cover bg-[var(--bg-secondary)]"
                          />
                        ) : (
                          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-[var(--bg-secondary)] text-[var(--accent-primary)]">
                            <Megaphone size={22} aria-hidden />
                          </span>
                        )}
                        <span className="min-w-0 flex-1 space-y-1">
                          <span
                            className={[
                              'block truncate text-sm leading-snug',
                              isUnread ? 'font-bold' : 'font-medium opacity-90',
                            ].join(' ')}
                          >
                            {title}
                          </span>
                          {dateLabel ? (
                            <time
                              className={[
                                'block text-[11px] tabular-nums text-[var(--text-secondary)]',
                                isUnread ? 'font-bold' : 'font-normal',
                              ].join(' ')}
                            >
                              {dateLabel}
                            </time>
                          ) : null}
                          {teaser ? (
                            <span
                              className={[
                                'block truncate text-xs leading-relaxed text-[var(--text-secondary)]',
                                isUnread ? 'font-medium' : 'font-normal',
                              ].join(' ')}
                            >
                              {teaser}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  }, [active, closeAll, lang, listAnnouncements, listOpen, openDetail, readAtById]);

  return {
    announcements,
    unreadCount,
    hasNews: announcements.length > 0,
    openList,
    NewsOverlays,
  };
}

type VisitingCardNewsProviderProps = {
  lang: Language;
  tenantSlug: string;
  /** Extra bottom offset when a sticky dock is present. */
  dockOffset?: boolean;
  children: ReactNode;
};

export function VisitingCardNewsProvider({
  lang,
  tenantSlug,
  dockOffset = false,
  children,
}: VisitingCardNewsProviderProps) {
  const news = useVisitingCardNewsState(lang, tenantSlug);
  const value = useMemo(
    () => ({
      announcements: news.announcements,
      unreadCount: news.unreadCount,
      hasNews: news.hasNews,
      openList: news.openList,
    }),
    [news.announcements, news.hasNews, news.openList, news.unreadCount],
  );

  return (
    <VisitingCardNewsContext.Provider value={value}>
      {/* Relative shell: news sheets/FAB stay inside the desktop phone bezel (not viewport-fixed). */}
      <div className="relative h-full w-full">
        {children}
        {news.hasNews ? (
          <VisitingCardNewsFab
            lang={lang}
            unread={news.unreadCount > 0}
            onOpen={news.openList}
            dockOffset={dockOffset}
          />
        ) : null}
        {news.NewsOverlays()}
      </div>
    </VisitingCardNewsContext.Provider>
  );
}

type NewsButtonProps = {
  lang: Language;
  unread: boolean;
  onOpen: () => void;
  variant?: 'dock' | 'pill' | 'icon' | 'bar' | 'grid';
};

/** Contact-row news control (all button templates). */
export function VisitingCardNewsButton({
  lang,
  unread,
  onOpen,
  variant = 'dock',
}: NewsButtonProps) {
  const label = _t('visitingCard.action.news', lang);
  const icon = (
    <span className={unread ? 'visiting-card-news-attention' : undefined}>
      <Megaphone size={variant === 'dock' || variant === 'icon' || variant === 'grid' ? 20 : 16} aria-hidden />
    </span>
  );

  if (variant === 'pill') {
    return (
      <button
        type="button"
        id="btn-visiting-card-news-pill"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1.5 rounded-full bg-[var(--accent-primary)] px-3 py-2 text-xs font-semibold text-[var(--accent-fg)]"
        aria-label={label}
      >
        {icon}
        {label}
      </button>
    );
  }

  if (variant === 'icon') {
    return (
      <button
        type="button"
        id="btn-visiting-card-news-icon"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        className="flex h-14 w-14 flex-col items-center justify-center rounded-full border border-[var(--accent-primary)]/35 bg-stone-50 text-[var(--accent-primary)] shadow-sm"
        aria-label={label}
        title={label}
      >
        {icon}
      </button>
    );
  }

  if (variant === 'bar') {
    return (
      <button
        type="button"
        id="btn-visiting-card-news-bar"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--accent-primary)] px-4 py-3 text-sm font-semibold text-[var(--accent-fg)]"
        aria-label={label}
      >
        {icon}
        {label}
      </button>
    );
  }

  if (variant === 'grid') {
    return (
      <button
        type="button"
        id="btn-visiting-card-news-grid"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-[var(--accent-primary)]/30 bg-white p-2 text-center text-[var(--accent-primary)]"
        aria-label={label}
      >
        {icon}
        <span className="text-[10px] font-semibold leading-tight">{label}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      id="btn-visiting-card-news-dock"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
      className="flex flex-col items-center gap-1 px-2 py-1"
      aria-label={label}
    >
      {icon}
      <span className="max-w-[4.5rem] truncate text-[9px] font-medium uppercase tracking-tight">
        {label}
      </span>
    </button>
  );
}

/** Always-visible floating entry — survives crowded contact rows. */
function VisitingCardNewsFab({
  lang,
  unread,
  onOpen,
  dockOffset,
}: {
  lang: Language;
  unread: boolean;
  onOpen: () => void;
  dockOffset: boolean;
}) {
  return (
    <button
      type="button"
      id="btn-visiting-card-news-fab"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
      aria-label={_t('visitingCard.action.news', lang)}
      title={_t('visitingCard.action.news', lang)}
      className={[
        'absolute z-40 flex h-11 w-11 items-center justify-center rounded-full',
        'bg-[var(--accent-primary)] text-[var(--accent-fg)] shadow-lg',
        'end-3',
        dockOffset
          ? 'bottom-[calc(4.5rem+env(safe-area-inset-bottom))]'
          : 'bottom-[max(1rem,env(safe-area-inset-bottom))]',
        unread ? 'visiting-card-news-fab-attention' : '',
      ].join(' ')}
    >
      <span className={unread ? 'visiting-card-news-attention' : undefined}>
        <Megaphone size={20} aria-hidden />
      </span>
    </button>
  );
}
